/**
 * Terms of Service Screen
 * 
 * Displays the Terms of Service for Tripvio.
 * This is an in-app version that can later be replaced with external URL links.
 */

import { Pressable, View, Platform, Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';

export default function TermsOfServiceScreen() {
  const router = useRouter();
  const theme = useTheme();

  return (
    <Screen
      title="Terms of Service"
      subtitle="Last updated: May 2026"
      headerAction={
        <Pressable
          onPress={() => router.back()}
          style={({ pressed }) => ({
            padding: theme.spacing(1),
            borderRadius: theme.radii.md,
            opacity: pressed ? 0.7 : 1,
          })}
          accessibilityLabel="Go back"
          accessibilityRole="button"
        >
          <Ionicons name="chevron-back" size={24} color={theme.colors.text} />
        </Pressable>
      }
    >
      <View style={{ gap: theme.spacing(3) }}>
        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Agreement to Terms</SubHeading>
          <Body color={theme.colors.textSecondary}>
            By accessing or using Tripvio ("the Service") available at tripvio.app and through our mobile applications, you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Age Requirement</SubHeading>
          <Body color={theme.colors.textSecondary}>
            You must be at least 18 years old to use Tripvio. By creating an account, you represent and warrant that you are at least 18 years of age and have the legal capacity to enter into binding agreements. If you are under 18, you may not use the Service.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Use License</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Permission is granted to temporarily use Tripvio for personal, non-commercial purposes. This is the grant of a license, not a transfer of title, and under this license you may not:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2) }}>
            <Body color={theme.colors.textSecondary}>
              • Modify or copy the materials
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Use the materials for any commercial purpose
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Attempt to reverse engineer any software contained in the Service
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Remove any copyright or other proprietary notations
            </Body>
          </View>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>User Accounts</SubHeading>
          <Body color={theme.colors.textSecondary}>
            When you create an account with us, you must provide information that is accurate, complete, and current at all times. You are responsible for safeguarding the password and for all activities that occur under your account.
          </Body>
          <Body color={theme.colors.textSecondary}>
            You agree not to disclose your password to any third party and to take sole responsibility for any activities or actions under your account.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>User Content</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Our Service allows you to post, link, store, share, and otherwise make available certain information, text, graphics, or other material ("Content"). You are responsible for the Content that you post to the Service.
          </Body>
          <Body color={theme.colors.textSecondary}>
            You retain ownership of any Content you submit, post, or display on or through the Service. By submitting Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, and distribute your Content solely for the purpose of providing the Service.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Prohibited Uses</SubHeading>
          <Body color={theme.colors.textSecondary}>
            You may not use the Service:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2) }}>
            <Body color={theme.colors.textSecondary}>
              • In any way that violates any applicable law or regulation
            </Body>
            <Body color={theme.colors.textSecondary}>
              • To transmit any malicious code or viruses
            </Body>
            <Body color={theme.colors.textSecondary}>
              • To impersonate or attempt to impersonate another user
            </Body>
            <Body color={theme.colors.textSecondary}>
              • In any manner that could disable, overburden, or impair the Service
            </Body>
            <Body color={theme.colors.textSecondary}>
              • To engage in any automated use of the Service without authorization
            </Body>
          </View>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Email Access</SubHeading>
          <Body color={theme.colors.textSecondary}>
            When you connect your email account to Tripvio, you grant us permission to access your email solely for the purpose of detecting travel-related bookings and automatically creating trips. We will only access emails necessary for this functionality and will not read, store, or share your email content beyond what is required for trip detection.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Service Availability</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We reserve the right to withdraw or amend the Service, and any service or material we provide through the Service, in our sole discretion without notice. We will not be liable if, for any reason, all or any part of the Service is unavailable at any time or for any period.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Service Disclaimer</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Tripvio is a trip planning and organization tool. We do not book, sell, or provide travel services, accommodations, flights, or other travel-related services. We are not a travel agency, booking platform, or travel service provider. Any travel bookings, reservations, or transactions are made directly between you and third-party service providers. We are not responsible for the quality, safety, legality, or accuracy of any travel services provided by third parties.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            We do not guarantee the accuracy, completeness, or timeliness of any travel information, including but not limited to flight schedules, hotel availability, pricing, or travel advisories. You are solely responsible for verifying all travel information and making your own travel arrangements.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Third-Party Services and Links</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Our Service may contain links to third-party websites, services, or applications that are not owned or controlled by Tripvio. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party services. You acknowledge and agree that Tripvio shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such third-party services.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            We strongly advise you to read the terms and conditions and privacy policies of any third-party services that you visit or use. Your interactions with third-party services are solely between you and the third party.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Affiliate and partner links</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Certain areas of the Service may display links, recommendations, or shortcuts to third-party travel and related services (for example, insurance, connectivity, tickets, or transportation). Some of these links are affiliate or referral links. When you use them, Tripvio may receive a commission, fee, or other compensation from the partner or an affiliate network at no additional cost to you, depending on the partner’s pricing rules.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            Tripvio does not control partner websites, pricing, availability, or fulfillment. Any booking, purchase, or contract is between you and the partner (or its suppliers). We may label affiliate placements in the app where appropriate; see also our Privacy Policy for how related interactions may be processed.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Disclaimer</SubHeading>
          <Body color={theme.colors.textSecondary}>
            The information on the Service is provided on an "as is" basis. To the fullest extent permitted by law, we exclude all representations, warranties, and conditions relating to the Service and the use of the Service.
          </Body>
          <Body color={theme.colors.textSecondary}>
            We do not warrant that the Service will be available, uninterrupted, secure, or error-free, or that defects will be corrected.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Limitation of Liability</SubHeading>
          <Body color={theme.colors.textSecondary}>
            To the maximum extent permitted by law, in no event shall Tripvio, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Service or inability to use the Service.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            Our total liability to you for all claims arising from or related to the Service shall not exceed the amount you paid us in the twelve (12) months preceding the claim, or $100, whichever is greater. Some jurisdictions do not allow the exclusion or limitation of certain damages, so some of the above limitations may not apply to you.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Limitation Period for Claims</SubHeading>
          <Body color={theme.colors.textSecondary}>
            You agree that any claim or cause of action arising out of or related to these Terms or your use of the Service must be filed within one (1) year after such claim or cause of action arose, or be forever barred, except where a longer limitation period is required by applicable law.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Indemnification</SubHeading>
          <Body color={theme.colors.textSecondary}>
            You agree to defend, indemnify, and hold harmless Tripvio and its licensees and licensors from and against any claims, actions, or demands, including without limitation reasonable legal and accounting fees, arising from your use of the Service or your breach of these Terms.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Termination</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
          </Body>
          <Body color={theme.colors.textSecondary}>
            Upon termination, your right to use the Service will immediately cease.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Account Deletion</SubHeading>
          <Body color={theme.colors.textSecondary}>
            You have the right to delete your account at any time. You can delete your account directly from your profile settings in the app, or by contacting us at legal@tripvio.app.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            When you delete your account, we will permanently delete or anonymize all of your personal information, including but not limited to:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2), marginTop: theme.spacing(0.5) }}>
            <Body color={theme.colors.textSecondary}>
              • Your profile information and account data
            </Body>
            <Body color={theme.colors.textSecondary}>
              • All trips you own
            </Body>
            <Body color={theme.colors.textSecondary}>
              • All bookings, expenses, and itinerary items
            </Body>
            <Body color={theme.colors.textSecondary}>
              • All documents and photos
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Your membership in collaborative trips (you will be removed)
            </Body>
          </View>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            Account deletion is permanent and cannot be undone. Some information may be retained as required by law or for legitimate business purposes (such as transaction records for accounting purposes), but will be anonymized where possible.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Intellectual Property</SubHeading>
          <Body color={theme.colors.textSecondary}>
            The Service and its original content, features, and functionality are owned by Tripvio and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Governing Law and Dispute Resolution</SubHeading>
          <Body color={theme.colors.textSecondary}>
            These Terms shall be interpreted and governed by the laws of the State of Delaware, United States, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration administered by the American Arbitration Association in accordance with its Commercial Arbitration Rules, except where prohibited by law. The arbitration shall take place in Delaware. You waive any right to a jury trial and to participate in a class action lawsuit.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            Notwithstanding the foregoing, either party may seek injunctive relief in any court of competent jurisdiction to protect intellectual property rights or to prevent irreparable harm.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Changes to Terms</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
          </Body>
          <Body color={theme.colors.textSecondary}>
            What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Severability</SubHeading>
          <Body color={theme.colors.textSecondary}>
            If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Force Majeure</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We shall not be liable for any failure or delay in performance under these Terms due to circumstances beyond our reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, labor disputes, government actions, internet or telecommunications failures, or other force majeure events.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Assignment</SubHeading>
          <Body color={theme.colors.textSecondary}>
            You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign these Terms or any rights hereunder without your consent to any third party in connection with a merger, acquisition, or sale of assets.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Waiver</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Our failure to enforce any right or provision of these Terms will not be considered a waiver of such right or provision. The waiver of any such right or provision will be effective only if in writing and signed by a duly authorized representative of Tripvio.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Electronic Communications</SubHeading>
          <Body color={theme.colors.textSecondary}>
            By using the Service, you consent to receive electronic communications from us. You agree that all agreements, notices, disclosures, and other communications that we provide to you electronically satisfy any legal requirement that such communications be in writing.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Entire Agreement</SubHeading>
          <Body color={theme.colors.textSecondary}>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and Tripvio regarding the use of the Service and supersede all prior agreements and understandings, whether written or oral.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Refund Policy</SubHeading>
          <Body color={theme.colors.textSecondary}>
            If Tripvio offers paid features, subscriptions, or premium services, the following refund policy applies:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2) }}>
            <Body color={theme.colors.textSecondary}>
              • Subscription fees are generally non-refundable, except as required by law or as explicitly stated at the time of purchase.
            </Body>
            <Body color={theme.colors.textSecondary}>
              • If you cancel a subscription, you will continue to have access to paid features until the end of your current billing period.
            </Body>
            <Body color={theme.colors.textSecondary}>
              • One-time purchases (such as template purchases) are generally non-refundable unless the product is defective or not as described.
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Refund requests must be submitted within 14 days of purchase and will be evaluated on a case-by-case basis.
            </Body>
          </View>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            To request a refund, please contact us at legal@tripvio.app with your purchase details and reason for the refund request.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Contact Information</SubHeading>
          <Body color={theme.colors.textSecondary}>
            If you have any questions about these Terms of Service, please contact us at:
          </Body>
          <Body color={theme.colors.primary} style={{ fontWeight: '600' }}>
            legal@tripvio.app
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            Website: tripvio.app
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            For legal notices, please send correspondence to the email address above. We will respond to your inquiry within 30 days, or as required by applicable law.
          </Body>
        </View>

        <View style={{ marginTop: theme.spacing(2), paddingTop: theme.spacing(2), borderTopWidth: 1, borderTopColor: theme.colors.borderMuted }}>
          <Pressable
            onPress={() => {
              const webUrl = 'https://tripvio.app/terms';
              if (Platform.OS === 'web') {
                if (typeof window !== 'undefined') {
                  window.open(webUrl, '_blank');
                }
              } else {
                Linking.openURL(webUrl).catch((err) => {
                  console.error('Failed to open terms of service URL:', err);
                });
              }
            }}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(1),
              padding: theme.spacing(1.5),
              borderRadius: theme.radii.md,
              backgroundColor: pressed ? theme.colors.surfaceVariant : 'transparent',
              opacity: pressed ? 0.7 : 1,
            })}
            accessibilityRole="link"
            accessibilityLabel="View Terms of Service on web"
          >
            <Ionicons name="open-outline" size={18} color={theme.colors.primary} />
            <ThemedText variant="body" color={theme.colors.primary} style={{ textDecorationLine: 'underline' }}>
              View these terms on web
            </ThemedText>
          </Pressable>
        </View>
      </View>
    </Screen>
  );
}















